import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { createStructuredSelector } from 'reselect';
import { ConnectedDpPaxContent } from './DpPaxContent';
import { withPageContext, TrackingUtils, CurrentAccommodationSearchSessionStorage, Config } from '../../../utils';
import { deleteRoom, updateNonPrimeHotel, updateRoom } from '../../../redux/currentSearch/actions';
import { PaxConstants, RoomsPaxProps } from '../../../models';
import { getNonPrimeHotelRoom, getResidentDiscountProps } from '../../../redux/currentSearch/selectors';
import { getDeleteRoomSelectorClassNames } from '../useRoomSelectorClassNames';

const KIDS_LENGTH_TRIGGER_TRACKING = 1;

export const DpPaxSelector = ({
    deleteRoom,
    updateRoom,
    room,
    roomNumber,
    trackEvent,
    numPax,
    hasError,
    isNative,
    isNonPrimeHotel,
    nonPrimeHotelRooms,
    updateNonPrimeHotel,
    showRoomHeader,
    paxRestrictions,
    residentProps,
    kidsAgeRange
}) => {
    const { t } = useTranslation();
    const residentZones = Config.get('residentZones');
    const onClickDelete = () => {
        handleTracking(TrackingUtils.LABEL.roomAction({ action: 'remove' }));
        deleteRoom(room);
        CurrentAccommodationSearchSessionStorage.deleteRoom(room);
    };

    const handleTracking = (label) => {
        const action = TrackingUtils.ACTION.DP;
        trackEvent({ action, label });
    };

    const onSelectKidAge = (id, selectedItem) => {
        const newRoom = isNonPrimeHotel ? { ...nonPrimeHotelRooms } : { ...room };
        newRoom.kids[id] = selectedItem;
        isNonPrimeHotel ? updateNonPrimeHotel(newRoom) : updateRoom(newRoom);
    };

    const onPaxChange = (partial) => {
        let newRoom = {};
        if (partial[PaxConstants.ADULTS]) {
            newRoom = { ...room, ...partial };
        } else {
            newRoom = { ...room };
            if (room.kids.length < partial.kids) {
                newRoom.kids.push(-1);
            } else {
                newRoom.kids.pop();
            }
        }

        if (newRoom.kids.length > KIDS_LENGTH_TRIGGER_TRACKING) {
            handleTracking(TrackingUtils.LABEL.maxKidsPerAdult());
        }
        updateRoom(newRoom);
        CurrentAccommodationSearchSessionStorage.setRooms(newRoom);
    };

    const onNonPrimeHotelChange = (partial) => {
        let newRoom = {};
        if (partial[PaxConstants.ADULTS]) {
            newRoom = { ...nonPrimeHotelRooms, ...partial };
        } else if (partial[PaxConstants.ROOMS]) {
            if (nonPrimeHotelRooms.rooms < partial.rooms) {
                const scenario = `_sce:${TrackingUtils.LABEL.userScenario()}`;
                trackEvent({
                    action: TrackingUtils.ACTION.HOTELS,
                    label: TrackingUtils.LABEL.roomAction({ action: 'add', scenario }),
                });
            }
            newRoom = { ...nonPrimeHotelRooms, ...partial };
        } else {
            newRoom = { ...nonPrimeHotelRooms };
            if (nonPrimeHotelRooms.kids.length < partial.kids) {
                newRoom.kids.push(-1);
            } else {
                newRoom.kids.pop();
            }
        }

        updateNonPrimeHotel(newRoom);
    };
    const contentProps = isNonPrimeHotel ? nonPrimeHotelRooms : { ...room };
    const deleteRoomClass = getDeleteRoomSelectorClassNames();
    const residentZonesObj = JSON.parse(residentZones);

    return (
        <>
            {showRoomHeader &&(
                <div className={deleteRoomClass.wrapper}>
                    <div className="odf-flex-container-row odf-flex-container-gutter-lg">
                        <div className="odf-flex-item dp-pax-selector-header">
                            {t('roomSelector.header')} {roomNumber + 1}
                        </div>
                        {roomNumber > 0 && (
                            <span
                                className={deleteRoomClass.icon}
                                onClick={onClickDelete}
                            />
                        )}
                    </div>
                </div>
            )}
            <ConnectedDpPaxContent
                isNonPrimeHotel={isNonPrimeHotel}
                pax={contentProps}
                paxRestrictions={paxRestrictions}
                roomId={room.id}
                onChange={isNonPrimeHotel ? onNonPrimeHotelChange : onPaxChange}
                onSelectKidAge={onSelectKidAge}
                showKidError={hasError}
                isNative={isNative}
                numPax={numPax}
                residentZones={residentZonesObj}
                residentProps={residentProps}
                kidsAgeRange={kidsAgeRange}
            />
        </>
    );
};

DpPaxSelector.propTypes = {
    room: RoomsPaxProps.isRequired,
    roomNumber: PropTypes.number.isRequired,
    deleteRoom: PropTypes.func.isRequired,
    updateRoom: PropTypes.func.isRequired,
    trackEvent: PropTypes.func.isRequired,
    numPax: PropTypes.number.isRequired,
    hasError: PropTypes.bool,
    isNative: PropTypes.bool,
    isNonPrimeHotel: PropTypes.bool,
    showRoomHeader: PropTypes.bool,
    nonPrimeHotelRooms: PropTypes.object.isRequired,
    updateNonPrimeHotel: PropTypes.func.isRequired,
    residentProps: PropTypes.object.isRequired,
    kidsAgeRange: PropTypes.arrayOf(PropTypes.number)
};

DpPaxSelector.defaultProps = {
    hasError: false,
    isNative: false,
};

const mapStateToPropsSelector = createStructuredSelector({
    nonPrimeHotelRooms: getNonPrimeHotelRoom,
    residentProps: getResidentDiscountProps,
});

const ConnectedDpPaxSelector = connect(mapStateToPropsSelector, {
    deleteRoom,
    updateRoom,
    updateNonPrimeHotel,
})(DpPaxSelector);

export default withPageContext(ConnectedDpPaxSelector);
